/**
 * Eingegangene Mails tragen fast immer den ganzen bisherigen Verlauf unter sich. Im Postfach
 * steht dieser Verlauf aber schon, Mail für Mail darüber — angehängt bläht er jede Antwort auf
 * das Zehnfache auf (bei Focus: 15 Zeilen Antwort, 150 Zeilen Zitat).
 *
 * `splitQuotedReply` trennt die Mail an der Stelle, an der das Zitat beginnt. Gekürzt wird nur
 * in der Anzeige; gespeichert bleibt die ganze Mail, und das Zitat lässt sich aufklappen. Eine
 * Signatur wird bewusst nicht erkannt: dafür gibt es kein verlässliches Zeichen, und eine
 * abgeschnittene Antwort wäre der teurere Fehler.
 */

export interface QuotedReply {
  /** Was der Absender geschrieben hat. */
  reply: string;
  /** Ab der Einleitung des Zitats bis zum Ende; `null`, wenn keins erkannt wurde. */
  quoted: string | null;
}

/** Eine Zeile (oder zwei, umbrochen), die ein Zitat einleitet. */
const HEADER_PATTERNS: RegExp[] = [
  // „Am 01.10.2026 um 08:26 schrieb Anja <…>:", „FOCUS Bikes GmbH <…> schrieb am Do. 1. Okt. 2026 um 08:26:"
  /\bschrieb\b.{0,120}:\s*$/i,
  // „On Thu, Oct 1, 2026 at 8:26 AM Anja <…> wrote:"
  /\bwrote\s*:\s*$/i,
  // Apple Mail und andere: „Mittwoch, 30. September 2026, 16:35:37 +0200, Name <adresse>:"
  /\b(19|20)\d\d\b.*<[^<>\s]+@[^<>\s]+>\s*:\s*$/,
  /^-{2,}\s*(Original Message|Ursprüngliche Nachricht|Original-Nachricht|Originalnachricht|Forwarded message|Weitergeleitete Nachricht)\s*-{2,}/i,
];

/** Outlook schreibt statt einer Zeile einen Kopfblock: „Von: …" und kurz darauf „Gesendet: …". */
const OUTLOOK_FROM = /^\*?(Von|From)\s*:\*?\s+\S/i;
const OUTLOOK_FOLLOW = /^\*?(Gesendet|Sent|Datum|Date|An|To|Betreff|Subject)\s*:/i;

function isQuoteLine(line: string): boolean {
  return /^\s*>/.test(line);
}

function headerAt(lines: string[], i: number): boolean {
  const line = lines[i].trim();
  if (!line) return false;
  const joined = i + 1 < lines.length ? `${line} ${lines[i + 1].trim()}` : line;
  for (const re of HEADER_PATTERNS) {
    if (re.test(line)) return true;
    // Umbrochen auf zwei Zeilen: die erste endet noch nicht mit dem Doppelpunkt.
    if (!line.endsWith(':') && re.test(joined)) return true;
  }
  if (OUTLOOK_FROM.test(line)) {
    for (let k = i + 1; k < Math.min(lines.length, i + 5); k++) {
      if (OUTLOOK_FOLLOW.test(lines[k].trim())) return true;
    }
  }
  return false;
}

export function splitQuotedReply(text: string): QuotedReply {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (headerAt(lines, i)) { cut = i; break; }
    // Ein Block aus `>`-Zeilen, nach dem nichts Eigenes mehr kommt. Ein einzelnes Zitat mitten
    // in der Antwort („> Wie stellst du dir das vor?" — „So:") bleibt stehen.
    if (isQuoteLine(lines[i]) && lines.slice(i).every(l => !l.trim() || isQuoteLine(l))) { cut = i; break; }
  }
  if (cut < 0) return { reply: text, quoted: null };
  const reply = lines.slice(0, cut).join('\n').replace(/\s+$/, '');
  // Nichts davor: dann ist es keine Antwort mit Zitat, sondern z. B. eine weitergeleitete Mail.
  if (!reply.trim()) return { reply: text, quoted: null };
  return { reply, quoted: lines.slice(cut).join('\n').replace(/\s+$/, '') };
}

/**
 * Die Adressen, an die eine Antwort gehen kann: die im Adressbuch und jede, die in diesem
 * Verlauf geschrieben hat. Vorgewählt ist der Absender der letzten eingegangenen Mail — wer
 * zuletzt geschrieben hat, wartet auf die Antwort. Dieselbe Auswahl prüft der Server
 * (`OutreachService.replyRecipient`).
 */
export function replyRecipients(
  contactEmail: string,
  messages: { direction: string; fromEmail?: string | null; occurredAt?: string }[],
): { options: string[]; preselected: string } {
  const options = [contactEmail];
  const inbound = messages
    .filter(m => m.direction === 'IN' && m.fromEmail)
    .sort((a, b) => new Date(a.occurredAt ?? 0).getTime() - new Date(b.occurredAt ?? 0).getTime());
  inbound.forEach(m => {
    const address = m.fromEmail as string;
    if (!options.some(o => o.toLowerCase() === address.toLowerCase())) options.push(address);
  });
  const last = inbound.length ? (inbound[inbound.length - 1].fromEmail as string) : contactEmail;
  const preselected = options.find(o => o.toLowerCase() === last.toLowerCase()) ?? contactEmail;
  return { options, preselected };
}

/**
 * Der Betreff einer Antwort: „Re: “ vor dem Betreff der Mail, auf die geantwortet wird — der
 * letzten von [toEmail], sonst der letzten eingegangenen überhaupt (dieselbe Wahl wie
 * `OutreachService.answeredMessage` für `In-Reply-To`). Trägt er schon ein Antwort-Präfix
 * („Re:", „AW:", „Antw.:"), bleibt er, wie er ist, statt zu „Re: Re: …“ zu wachsen. Ohne
 * eingegangene Mail gibt es nichts zu beantworten: leer.
 */
export function replySubject(
  messages: { direction: string; fromEmail?: string | null; subject?: string | null; occurredAt?: string }[],
  toEmail: string,
): string {
  const inbound = messages
    .filter(m => m.direction === 'IN' && (m.subject ?? '').trim())
    .sort((a, b) => new Date(a.occurredAt ?? 0).getTime() - new Date(b.occurredAt ?? 0).getTime());
  const fromRecipient = inbound.filter(m => (m.fromEmail ?? '').toLowerCase() === toEmail.toLowerCase());
  const answered = fromRecipient.length ? fromRecipient[fromRecipient.length - 1] : inbound[inbound.length - 1];
  if (!answered) return '';
  const subject = (answered.subject as string).trim();
  return /^(re|aw|antw|sv|vs)\s*\.?\s*:/i.test(subject) ? subject : `Re: ${subject}`;
}
